import type JSZip from "jszip";
import { toast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { loadFromCache } from "@/utils/stream-cache";
import { getConversationCacheKey } from "@/utils/dashboard-conversation-stream";

export type ExecutionBundleSource = {
    executionId: string;
    supportBundleUrl: string;
    token: string;
};

export type AssembleBundleOptions = {
    /** Environment-level support bundle URL. Omit if unavailable. */
    environmentBundle?: {
        supportBundleUrl: string;
        logsToken: string;
    };
    /** Agent execution bundles to include. Empty array = no agent data. */
    executionBundles: ExecutionBundleSource[];
    /** Filename for the downloaded zip. */
    filename: string;
};

/**
 * Downloads an environment support bundle and adds it to a zip file.
 * On failure, adds an error file to the zip instead of throwing.
 */
export async function downloadEnvironmentSupportBundle(
    zip: JSZip,
    supportBundleUrl: string,
    logsToken: string,
): Promise<void> {
    try {
        const supportBundleResponse = await fetch(supportBundleUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${logsToken}`,
            },
        });

        if (!supportBundleResponse.ok) {
            const errorMessage = `Failed to download environment support bundle: ${supportBundleResponse.status} ${supportBundleResponse.statusText}\nTimestamp: ${new Date().toISOString()}`;
            zip.file("environment-support-bundle-error.txt", errorMessage);
            return;
        }

        const supportBundleBlob = await supportBundleResponse.blob();
        zip.file("environment-support-bundle.zip", supportBundleBlob);
    } catch (error) {
        const errorMessage = `Failed to download environment support bundle: ${String(error)}\nTimestamp: ${new Date().toISOString()}`;
        zip.file("environment-support-bundle-error.txt", errorMessage);
    }
}

/**
 * Triggers a browser download for a blob with the given filename.
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

/**
 * Downloads an environment support bundle zip and triggers a browser save.
 * Optionally accepts a callback to add extra files to the zip before saving.
 */
export async function downloadAndSaveSupportBundle(options: {
    supportBundleUrl: string;
    logsToken: string;
    filename: string;
    skipEnvironmentBundle?: boolean;
    addExtras?: (zip: JSZip) => Promise<void>;
}): Promise<void> {
    const JSZipModule = (await import("jszip")).default;
    const zip = new JSZipModule();

    if (!options.skipEnvironmentBundle) {
        await downloadEnvironmentSupportBundle(zip, options.supportBundleUrl, options.logsToken);
    }

    if (options.addExtras) {
        await options.addExtras(zip);
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });
    triggerBlobDownload(zipBlob, options.filename);
}

/**
 * Loads cached conversation data from IndexedDB and adds it to the zip.
 * On failure or missing data, adds an info/error file instead.
 */
export async function downloadCachedConversationData(
    zip: JSZip,
    agentExecutionId: string,
    prefix: string,
): Promise<void> {
    try {
        const cacheKey = getConversationCacheKey(agentExecutionId);
        const cachedData = await loadFromCache(cacheKey);

        if (cachedData && cachedData.chunk.length > 0) {
            zip.file(`${prefix}conversation-stream.jsonl`, cachedData.chunk);
        } else {
            const message = `No cached conversation data available for agent execution ${agentExecutionId}\nThis contains the conversation data as last seen by the user.\nTimestamp: ${new Date().toISOString()}`;
            zip.file(`${prefix}conversation-stream-unavailable.txt`, message);
        }
    } catch (error) {
        const errorMessage = `Failed to load cached conversation data: ${String(error)}\nTimestamp: ${new Date().toISOString()}`;
        zip.file(`${prefix}conversation-stream-error.txt`, errorMessage);
    }
}

/**
 * Downloads an agent execution support bundle and adds it to a zip file.
 * On failure, adds an error file to the zip instead of throwing.
 */
export async function downloadAgentExecutionSupportBundle(
    zip: JSZip,
    supportBundleUrl: string,
    token: string,
): Promise<void> {
    await fetchAgentExecutionBundle(zip, { executionId: "", supportBundleUrl, token }, "");
}

/**
 * Fetches an agent execution support bundle and adds it to the zip under the given prefix.
 * On failure, adds an error file instead of throwing.
 */
async function fetchAgentExecutionBundle(
    zip: JSZip,
    source: ExecutionBundleSource,
    prefix: string,
): Promise<void> {
    try {
        const response = await fetch(source.supportBundleUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${source.token}`,
            },
        });

        if (!response.ok) {
            const errorMessage = `Failed to download agent execution support bundle: ${response.status} ${response.statusText}\nTimestamp: ${new Date().toISOString()}`;
            zip.file(`${prefix}agent-execution-support-bundle-error.txt`, errorMessage);
            return;
        }

        const blob = await response.blob();
        zip.file(`${prefix}agent-execution-support-bundle.zip`, blob);
    } catch (error) {
        const errorMessage = `Failed to download agent execution support bundle: ${String(error)}\nTimestamp: ${new Date().toISOString()}`;
        zip.file(`${prefix}agent-execution-support-bundle-error.txt`, errorMessage);
    }
}

/**
 * Assembles a complete support bundle from environment and agent execution sources,
 * composes them into a zip, and triggers a browser download.
 *
 * Individual source failures are recorded as error files in the zip (non-fatal).
 * Shows a toast during generation and on completion.
 */
export async function assembleCompleteSupportBundle(options: AssembleBundleOptions): Promise<void> {
    const generatingToast = toast({ title: "Generating support bundle...", indefinite: true });

    try {
        const JSZipModule = (await import("jszip")).default;
        const zip = new JSZipModule();

        // Download all bundles in parallel — each source is independent and
        // individual failures are recorded as error files (non-fatal).
        const useNamespacing = options.executionBundles.length > 1;

        const downloads: Promise<void>[] = [];

        if (options.environmentBundle) {
            downloads.push(
                downloadEnvironmentSupportBundle(
                    zip,
                    options.environmentBundle.supportBundleUrl,
                    options.environmentBundle.logsToken,
                ),
            );
        }

        for (const source of options.executionBundles) {
            const prefix = useNamespacing ? `executions/${source.executionId}/` : "";
            downloads.push(fetchAgentExecutionBundle(zip, source, prefix));
            downloads.push(downloadCachedConversationData(zip, source.executionId, prefix));
        }

        await Promise.all(downloads);

        // 3. Generate and download
        const zipBlob = await zip.generateAsync({ type: "blob" });
        triggerBlobDownload(zipBlob, options.filename);

        generatingToast.dismiss();
        toast({ title: "Support bundle downloaded" });
    } catch (error) {
        generatingToast.dismiss();
        toast({
            title: "Failed to download support bundle",
            description: formatError(error),
        });
    }
}
